// src/pages/Receiver/GHG.js
// -----------------------------------------------------------------------------
// GHG Hub (Receiver)
// -----------------------------------------------------------------------------
// This page is the orchestration layer for the Receiver-side “GHG Hub” view.
// Responsibilities:
//   - Permission gating + top-level loading / error states
//   - Managing filter draft/applied state (via useGhgFiltersState)
//   - Fetching lightweight meta options (via useGhgMeta) for dropdowns
//   - Fetching heavy snapshot data ONLY after Apply (via useInventorySnapshot)
//   - Deriving all computed views (tables, charts, sankey data) from snapshot + edits
//   - Coordinating PPT export (expanding UI panels, forcing chart visibility, etc.)
//
// Architectural notes:
//   - “Meta” is intended to load quickly and power dropdown options even before Apply.
//   - “Snapshot” is intentionally heavy; it is disabled until filters.hasSearched.
//   - Receiver factors table edits override snapshot/material defaults during calculations.
//   - All derived data is memoized to avoid recomputation and keep UI responsive.
// -----------------------------------------------------------------------------


import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useDarkMode } from "../../contexts/ThemeContext";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import Alert from "../../components/ui/Alert";
import LastUpdatedBadge from "../../components/ui/LastUpdatedBadge";

// Snapshot fetch (heavy). Note the "enabled" gate below.
import { useInventorySnapshot } from "../../hooks/useInventorySnapshot.js";

// Permission guard ensures we show nothing if user cannot access this page.
import { usePermissionGuard } from "../../hooks/usePermissionGuard";

import "./GHGHub.css";

// Core building blocks (filters UI)
import RxGhgFilters from "./GHG/components/RxGhgFilters";

// Modular panels (each panel is responsible for its own UI rendering)
import HeroSection from "./GHG/components/HeroSection";
import ReceiverFactorsPanel from "./GHG/components/ReceiverFactorsPanel";
import DonorAllocationsPanel from "./GHG/components/DonorAllocationsPanel";
import TrendChartPanel from "./GHG/components/TrendChartPanel";
import PickupsChartPanel from "./GHG/components/PickupsChartPanel";
import SankeyPanel from "./GHG/components/SankeyPanel";
import MaterialsChipsPanel from "./GHG/components/MaterialsChipsPanel";

// PPT export dialog + related hooks
import ExportPptDialog from "./GHG/components/ExportPptDialog";
import { useGhgMeta } from "./GHG/hooks/useGhgMeta";
import { useGhgFiltersState } from "./GHG/hooks/useGhgFiltersState";

// PPT export implementation (captures charts / DOM nodes into a .pptx)
import { exportReportPpt } from "./GHG/components/exportReportPpt";

// API helpers / constants
import { API, postJson } from "./GHG/api";
import { DEFAULT_UNIT } from "./GHG/constants";

// Shared numeric/date helpers (keep this page as a composition layer only)
import {
  numFromAny,               // Parses numeric input from user/API safely
  inRange,                  // Range filtering helper
  fmt,                      // Numeric formatting helper
  nextRowId,                // Generates unique row_id for table rows
  parseDateSafe,            // Parses date/time safely from API strings
  parseLocalDateOnly,       // Parses YYYY-MM-DD (local date, no time)
  isWithinLocalDateRange,   // Local date range inclusion check
  bucketKey,                // Creates a “bucket” label for day/week/month grouping
  asNiceNumber,             // Formats / rounds without scientific notation surprises
} from "./GHG/utils";

/* =============================================================================
   PAGE COMPONENT
============================================================================= */

export default function GHG() {
  const { t } = useTranslation();

  /* ---------------------------------------------------------------------------
     Theme + permission gate
     - We do not render the page at all if user lacks permission.
     - While checking permissions, show a full-page spinner.
  --------------------------------------------------------------------------- */
  const { darkMode } = useDarkMode();
  const { hasPermission, isChecking } = usePermissionGuard("receiver", "ghg");

  /* ---------------------------------------------------------------------------
     Root container ref
     - Useful for future: scroll capture, DOM measurements, export, etc.
  --------------------------------------------------------------------------- */
  const rootRef = useRef(null);

  /* ---------------------------------------------------------------------------
     Chart refs for PPT capture
     - These refs are passed into chart panels and ultimately used during export.
     - exportReportPpt expects actual DOM elements to screenshot/serialize.
  --------------------------------------------------------------------------- */
  const trendChartRef = useRef(null);
  const pickupsChartRef = useRef(null);
  const sankeyQtyRef = useRef(null);
  const sankeyAvoidedRef = useRef(null);

  /* ---------------------------------------------------------------------------
     Sankey accordion ref
     - Parent controls expansion during export to ensure the chart is visible
     - SankeyPanel exposes a ref contract (expand/open/setExpanded) OR a DOM node.
  --------------------------------------------------------------------------- */
  const sankeyAccordionRef = useRef(null);

  /* ---------------------------------------------------------------------------
     Local UI state
  --------------------------------------------------------------------------- */
  const [isVisible, setIsVisible] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(""); // Top-level error banner
  const [emissionsPeriod, setEmissionsPeriod] = useState("month"); // bucket granularity

  /* ---------------------------------------------------------------------------
     FILTER STATE (MODULAR)
     - Draft state: what the user is editing in the filter UI
     - Applied state: what drives actual data and computed outputs
     - hasSearched: becomes true when Apply is clicked at least once
  --------------------------------------------------------------------------- */
  const filters = useGhgFiltersState();

  /* ---------------------------------------------------------------------------
     META (lightweight)
     - Provides dropdown options without requiring snapshot Apply.
     - Also may include a small “meta snapshot” depending on backend behavior.
  --------------------------------------------------------------------------- */
  const {
    metaData,
    metaLoading,
    metaError,
    materialOptions: materialOptionsFromMeta,
    materialEmissionsMap: materialEmissionsMapFromMeta,
  } = useGhgMeta();

  /* ---------------------------------------------------------------------------
     SNAPSHOT (heavy)
     - Enabled ONLY after Apply to prevent expensive fetch + reduce noise.
  --------------------------------------------------------------------------- */
  const snapshotEnabled = filters.hasSearched;
  const {
    data: invData,
    loading: invLoading,
    error: invError,
    lastUpdated: invLastUpdated,
    performRefresh: invPerformRefresh,
  } = useInventorySnapshot(true, snapshotEnabled, "snapshot");

  /* ---------------------------------------------------------------------------
     EXPORT STATE
     - Used to suppress animations or show “export in progress” UI if needed.
  --------------------------------------------------------------------------- */
  const [isExporting, setIsExporting] = useState(false);

  /* ---------------------------------------------------------------------------
     PICKUPS METRIC (UI)
     - The pickups chart can display count or quantity; export may force "qty".
  --------------------------------------------------------------------------- */
  const [pickupsMetric, setPickupsMetric] = useState("count");

  /* ---------------------------------------------------------------------------
     Report date range (YYYY-MM-DD strings)
     - Controls trend computation + what gets exported in the PPT.
  --------------------------------------------------------------------------- */
  const [reportFromStr, setReportFromStr] = useState("");
  const [reportToStr, setReportToStr] = useState("");

  /* ---------------------------------------------------------------------------
     RECEIVER TABLE (material emissions factors)
     - receiverRows: editable list used to override emission factors
     - receiverRowsFiltered: derived subset used by panel display
  --------------------------------------------------------------------------- */
  const [receiverRows, setReceiverRows] = useState([]);
  const [receiverSelection, setReceiverSelection] = useState([]); // selected row_ids
  const [receiverSaving, setReceiverSaving] = useState(false);

  /* ---------------------------------------------------------------------------
     DONOR TABLE (allocations)
     - donorRows can be seeded from snapshot and then edited by user
     - donorRowsFiltered: derived subset used by panel display
  --------------------------------------------------------------------------- */
  const [donorRows, setDonorRows] = useState([]);
  const [donorSelection, setDonorSelection] = useState([]); // selected row_ids
  const [donorSaving, setDonorSaving] = useState(false);

  /* ---------------------------------------------------------------------------
     SNAPSHOT ARRAYS
     - Only meaningful after Apply (filters.hasSearched).
     - Memoized to keep referential stability for downstream memos.
  --------------------------------------------------------------------------- */
  const listings = useMemo(
    () =>
      filters.hasSearched && Array.isArray(invData?.listings)
        ? invData.listings
        : [],
    [invData, filters.hasSearched]
  );

  const requests = useMemo(
    () =>
      filters.hasSearched && Array.isArray(invData?.requests)
        ? invData.requests
        : [],
    [invData, filters.hasSearched]
  );

  /* ---------------------------------------------------------------------------
     listingById
     - Many computations join requests -> listing data.
     - Use a Map for O(1) lookup by listing_id.
  --------------------------------------------------------------------------- */
  const listingById = useMemo(() => {
    const map = new Map();
    (listings || []).forEach((l) => map.set(String(l.id), l));
    return map;
  }, [listings]);

  /* ---------------------------------------------------------------------------
     MATERIAL EMISSIONS MAP
     - Prefer snapshot-provided map (most accurate / scoped to snapshot)
     - Fall back to meta-provided map for dropdown and initial display
  --------------------------------------------------------------------------- */
  const materialEmissionsObj = useMemo(() => {
    if (filters.hasSearched) return invData?.filters?.materialEmissionsMap || {};
    return materialEmissionsMapFromMeta || {};
  }, [invData, materialEmissionsMapFromMeta, filters.hasSearched]);

  /* ---------------------------------------------------------------------------
     materialListAll
     - All known materials from emissions map keys, stable sorted list.
  --------------------------------------------------------------------------- */
  const materialListAll = useMemo(
    () =>
      Object.keys(materialEmissionsObj || {}).sort((a, b) =>
        a.localeCompare(b)
      ),
    [materialEmissionsObj]
  );

  /* ---------------------------------------------------------------------------
     OPTIONS FOR FILTER DROPDOWNS
     - donorOptions:
       1) Use explicit meta list if backend provides it.
       2) Else derive from meta listings (so user can select donors BEFORE Apply).
       3) Else derive from donorRows (after Apply).
     - materialOptions:
       Combine meta list + emissions map keys + user-edited receiver rows.
  --------------------------------------------------------------------------- */
  const donorOptions = useMemo(() => {
    // 1) Best: backend sends a donors array (explicit, already sanitized)
    const metaList =
      metaData?.filters?.donors ||
      metaData?.filters?.donorOptions ||
      metaData?.filters?.companies;

    if (Array.isArray(metaList) && metaList.length > 0) {
      return metaList
        .map((x) => String(x || "").trim())
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
    }

    // 2) Next best: derive donors from meta listings (if present)
    //    This enables donor dropdown population before snapshot Apply.
    const metaListings = Array.isArray(metaData?.listings) ? metaData.listings : [];
    if (metaListings.length > 0) {
      const set = new Set();
      metaListings.forEach((l) => {
        const donor =
          l?.company ||
          l?.company_name ||
          l?.donor ||
          l?.donor_company ||
          l?.organization ||
          "";
        const name = String(donor || "").trim();
        if (name) set.add(name);
      });
      return Array.from(set).sort((a, b) => a.localeCompare(b));
    }

    // 3) Fallback: derive from donorRows (works after Apply)
    const set = new Set();
    (donorRows || []).forEach((r) => {
      const name = String(r?.donor || "").trim();
      if (name) set.add(name);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [metaData, donorRows]);

  const materialOptions = useMemo(() => {
    const set = new Set();

    // 1) Meta list (if backend provides)
    (materialOptionsFromMeta || []).forEach((m) => set.add(String(m)));

    // 2) Emissions map keys (often the canonical source of material types)
    (materialListAll || []).forEach((m) => set.add(String(m)));

    // 3) User-edited receiver rows (supports user adding new material names)
    (receiverRows || []).forEach((r) => {
      if (r?.name && String(r.name).trim() !== "") set.add(String(r.name));
    });

    // Ensure uniqueness, no blanks, stable sorting
    return Array.from(set)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }, [materialOptionsFromMeta, materialListAll, receiverRows]);

  /* ---------------------------------------------------------------------------
     receiverFactorsByMaterial
     - Map of material name -> edited factor values.
     - IMPORTANT: This is the override layer on top of snapshot/meta emissions map.
  --------------------------------------------------------------------------- */
  const receiverFactorsByMaterial = useMemo(() => {
    const m = new Map();
    (receiverRows || []).forEach((r) => {
      const name = String(r?.name ?? "").trim();
      if (!name) return;

      m.set(name, {
        emission_factor: r?.emission_factor ?? "",
        recycling_emissions: r?.recycling_emissions ?? "",
        emission_unit: DEFAULT_UNIT, // enforced unit for receiver-side edits
        energy_consumption_kwh: r?.energy_consumption_kwh ?? "",
        waste_stage_qty: r?.waste_stage_qty ?? "",
      });
    });
    return m;
  }, [receiverRows]);

  /* ---------------------------------------------------------------------------
     getEmissionsForMaterial(materialName)
     - Unified accessor for emission factors with precedence:
       1) Edited receiver table value (receiverFactorsByMaterial)
       2) Snapshot/meta emissions map (materialEmissionsObj)
       3) Defaults (empty values + DEFAULT_UNIT)
  --------------------------------------------------------------------------- */
  const getEmissionsForMaterial = useCallback(
    (mat) => {
      const key = String(mat ?? "").trim();

      // 1) Receiver-edited override
      const fromReceiver = receiverFactorsByMaterial.get(key);
      if (fromReceiver) {
        return {
          emission_factor: fromReceiver.emission_factor ?? "",
          recycling_emissions: fromReceiver.recycling_emissions ?? "",
          emission_unit: DEFAULT_UNIT,
          energy_consumption_kwh: fromReceiver.energy_consumption_kwh ?? "",
          waste_stage_qty: fromReceiver.waste_stage_qty ?? "",
        };
      }

      // 2) Snapshot/meta-provided emissions factors
      const e = materialEmissionsObj?.[key];
      return {
        emission_factor: e?.emission_factor ?? "",
        recycling_emissions: e?.recycling_emissions ?? "",
        emission_unit: e?.emission_unit ?? DEFAULT_UNIT,
        energy_consumption_kwh: e?.energy_consumption_kwh ?? "",
        waste_stage_qty: e?.waste_stage_qty ?? "",
      };
    },
    [receiverFactorsByMaterial, materialEmissionsObj]
  );

  /* ---------------------------------------------------------------------------
     computeAvoidedEmissions(material, qty)
     - Returns *formatted string* for table display.
     - Avoided = qty * (emission_factor - recycling_emissions)
     - If inputs are not parseable, return empty string.
  --------------------------------------------------------------------------- */
  const computeAvoidedEmissions = useCallback(
    (material_type, qty) => {
      const e = getEmissionsForMaterial(material_type);
      const ef = numFromAny(e.emission_factor);
      const re = numFromAny(e.recycling_emissions);
      const q = numFromAny(qty);

      if (ef == null || re == null || q == null) return "";
      return fmt(q * (ef - re), 6);
    },
    [getEmissionsForMaterial]
  );

  /* ---------------------------------------------------------------------------
     computeAvoidedEmissionsNum(material, qtyNum)
     - Returns *number* for aggregation (charts/sankey).
     - If invalid, return null to signal “do not count”.
  --------------------------------------------------------------------------- */
  const computeAvoidedEmissionsNum = useCallback(
    (material_type, qtyNum) => {
      const e = getEmissionsForMaterial(material_type);
      const ef = numFromAny(e.emission_factor);
      const re = numFromAny(e.recycling_emissions);
      const q = Number(qtyNum);

      if (ef == null || re == null || !Number.isFinite(q)) return null;
      return q * (ef - re);
    },
    [getEmissionsForMaterial]
  );

  // page entrance animation (matches donor pattern)
  useEffect(() => {
    const timer = setTimeout(() => setInitialLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!initialLoading && !metaLoading) {
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    }
  }, [initialLoading, metaLoading]);

  // badge refresh — local state controls spinner, not hook loading
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await invPerformRefresh();
    } catch (e) {
      console.error("refresh error:", e);
    }
    setTimeout(() => setRefreshing(false), 2000);
  }, [invPerformRefresh]);

  /* ---------------------------------------------------------------------------
     AUTO-SEED RECEIVER ROWS
  --------------------------------------------------------------------------- */
  useEffect(() => {
    if (!filters.hasSearched) return;
    if (receiverRows.length > 0) return;
    if (materialListAll.length === 0) return;

    setReceiverRows(
      materialListAll.map((mat, i) => {
        const e = getEmissionsForMaterial(mat);
        return {
          row_id: i,
          name: mat,
          emission_factor: e.emission_factor,
          recycling_emissions: e.recycling_emissions,
          energy_consumption_kwh: e.energy_consumption_kwh,
          waste_stage_qty: e.waste_stage_qty,
        };
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialListAll, filters.hasSearched]);

  /* ---------------------------------------------------------------------------
     donorRowsFromSnapshot
  --------------------------------------------------------------------------- */
  const donorRowsFromSnapshot = useMemo(() => {
    if (!filters.hasSearched) return [];
    const agg = new Map();

    (requests || [])
      .filter((r) => Boolean(r?.date_delivered))
      .forEach((r) => {
        const listing = listingById.get(String(r.listing_id));
        if (!listing) return;

        const material =
          listing?.material ||
          listing?.materialType ||
          listing?.material_name ||
          listing?.material_type ||
          "Unknown";

        const donor =
          listing?.company ||
          listing?.company_name ||
          listing?.donor ||
          listing?.donor_company ||
          "—";

        if (!donor || donor === "—") return;

        const qty =
          Number(r?.weight ?? r?.quantity ?? listing?.weight ?? listing?.quantity) || 0;

        const key = `${donor}|||${material}`;
        agg.set(key, (agg.get(key) || 0) + qty);
      });

    const rows = [...agg.entries()].map(([key, qty], idx) => {
      const [donor, material_type] = key.split("|||");
      const qtyFixed = Number(qty || 0).toFixed(2);
      return {
        row_id: idx,
        donor,
        material_type,
        description: "",
        qty: qtyFixed,
        avoided_emissions: "",
      };
    });

    rows.sort((a, b) => {
      const d = String(a.donor).localeCompare(String(b.donor));
      if (d !== 0) return d;
      return String(a.material_type).localeCompare(String(b.material_type));
    });

    return rows.map((r, i) => ({ ...r, row_id: i }));
  }, [requests, listingById, filters.hasSearched]);

  /* ---------------------------------------------------------------------------
     AUTO-SEED DONOR ROWS
  --------------------------------------------------------------------------- */
  useEffect(() => {
    if (!filters.hasSearched) return;
    if (donorRows.length > 0) return;
    if (donorRowsFromSnapshot.length === 0) return;
    setDonorRows(donorRowsFromSnapshot);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [donorRowsFromSnapshot, filters.hasSearched]);

  /* ---------------------------------------------------------------------------
     Recompute avoided emissions for all donor rows after Apply / factor changes.
  --------------------------------------------------------------------------- */
  useEffect(() => {
    if (!filters.hasSearched) return;
    setDonorRows((prev) =>
      (prev || []).map((row) => ({
        ...row,
        avoided_emissions: computeAvoidedEmissions(row.material_type, row.qty),
      }))
    );
  }, [computeAvoidedEmissions, filters.hasSearched]);

  /* ---------------------------------------------------------------------------
     APPLY / CLEAR
  --------------------------------------------------------------------------- */
  const onApplyFilters = useCallback(() => {
    filters.apply();
  }, [filters]);

  const onClearFilters = useCallback(() => {
    filters.clear();
    setReceiverRows([]);
    setReceiverSelection([]);
    setDonorRows([]);
    setDonorSelection([]);
    setError("");
  }, [filters]);

  /* ---------------------------------------------------------------------------
     donorRowsAfterDonorMaterialFilters
  --------------------------------------------------------------------------- */
  const donorRowsAfterDonorMaterialFilters = useMemo(() => {
    const mats = new Set((filters.appliedMaterials || []).map(String));
    const dons = new Set((filters.appliedDonors || []).map(String));

    return (donorRows || []).filter((r) => {
      const donor = String(r?.donor ?? "");
      const material = String(r?.material_type ?? "");
      if (mats.size > 0 && !mats.has(material)) return false;
      if (dons.size > 0 && !dons.has(donor)) return false;
      return true;
    });
  }, [donorRows, filters.appliedMaterials, filters.appliedDonors]);

  /* ---------------------------------------------------------------------------
     usedMaterialsSet
  --------------------------------------------------------------------------- */
  const usedMaterialsSet = useMemo(() => {
    const s = new Set();
    donorRowsAfterDonorMaterialFilters.forEach((r) => {
      const m = String(r?.material_type ?? "");
      if (m) s.add(m);
    });
    return s;
  }, [donorRowsAfterDonorMaterialFilters]);

  /* ---------------------------------------------------------------------------
     receiverRowsFiltered
  --------------------------------------------------------------------------- */
  const receiverRowsFiltered = useMemo(() => {
    const mats = new Set((filters.appliedMaterials || []).map(String));
    const q = String(filters.appliedReceiverSearch || "").trim().toLowerCase();

    return (receiverRows || []).filter((r) => {
      const name = String(r?.name ?? "");
      if (mats.size > 0 && !mats.has(name)) return false;
      if (filters.appliedReceiverOnlyUsed && !usedMaterialsSet.has(name)) return false;
      if (q && !name.toLowerCase().includes(q)) return false;

      const ef = numFromAny(r?.emission_factor);
      const re = numFromAny(r?.recycling_emissions);
      const net = ef != null && re != null ? ef - re : null;

      if (filters.appliedEfMin != null || filters.appliedEfMax != null) {
        if (ef == null || !inRange(ef, filters.appliedEfMin, filters.appliedEfMax))
          return false;
      }
      if (filters.appliedReMin != null || filters.appliedReMax != null) {
        if (re == null || !inRange(re, filters.appliedReMin, filters.appliedReMax))
          return false;
      }
      if (filters.appliedNetMin != null || filters.appliedNetMax != null) {
        if (net == null || !inRange(net, filters.appliedNetMin, filters.appliedNetMax))
          return false;
      }
      return true;
    });
  }, [
    receiverRows,
    filters.appliedMaterials,
    filters.appliedReceiverSearch,
    filters.appliedReceiverOnlyUsed,
    usedMaterialsSet,
    filters.appliedEfMin,
    filters.appliedEfMax,
    filters.appliedReMin,
    filters.appliedReMax,
    filters.appliedNetMin,
    filters.appliedNetMax,
  ]);

  /* ---------------------------------------------------------------------------
     donorRowsFiltered
  --------------------------------------------------------------------------- */
  const donorRowsFiltered = useMemo(() => {
    const mats = new Set((filters.appliedMaterials || []).map(String));
    const dons = new Set((filters.appliedDonors || []).map(String));
    const q = String(filters.appliedDonorSearch || "").trim().toLowerCase();

    return (donorRows || []).filter((r) => {
      const donor = String(r?.donor ?? "");
      const material = String(r?.material_type ?? "");
      const desc = String(r?.description ?? "");

      if (mats.size > 0 && !mats.has(material)) return false;
      if (dons.size > 0 && !dons.has(donor)) return false;

      if (q) {
        const hay = `${donor} ${material} ${desc}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }

      const qty = numFromAny(r?.qty);
      const avoided = numFromAny(r?.avoided_emissions);

      if (filters.appliedQtyMin != null || filters.appliedQtyMax != null) {
        if (qty == null || !inRange(qty, filters.appliedQtyMin, filters.appliedQtyMax))
          return false;
      }
      if (filters.appliedAvoidedMin != null || filters.appliedAvoidedMax != null) {
        if (
          avoided == null ||
          !inRange(avoided, filters.appliedAvoidedMin, filters.appliedAvoidedMax)
        )
          return false;
      }

      return true;
    });
  }, [
    donorRows,
    filters.appliedMaterials,
    filters.appliedDonors,
    filters.appliedDonorSearch,
    filters.appliedQtyMin,
    filters.appliedQtyMax,
    filters.appliedAvoidedMin,
    filters.appliedAvoidedMax,
  ]);

  /* ---------------------------------------------------------------------------
     REPORT RANGE
  --------------------------------------------------------------------------- */
  const reportFromDate = useMemo(() => parseLocalDateOnly(reportFromStr), [reportFromStr]);
  const reportToDate = useMemo(() => parseLocalDateOnly(reportToStr), [reportToStr]);

  /* ---------------------------------------------------------------------------
     deliveredRequestsInReportRange
  --------------------------------------------------------------------------- */
  const deliveredRequestsInReportRange = useMemo(() => {
    return (requests || [])
      .filter((r) => Boolean(r?.date_delivered))
      .filter((r) => {
        const deliveredAt = parseDateSafe(r.date_delivered);
        if (!deliveredAt) return false;
        return isWithinLocalDateRange(deliveredAt, reportFromDate, reportToDate);
      });
  }, [requests, reportFromDate, reportToDate]);

  /* ---------------------------------------------------------------------------
     receiverMaterialQuantities
  --------------------------------------------------------------------------- */
  const receiverMaterialQuantities = useMemo(() => {
    const agg = new Map();

    deliveredRequestsInReportRange.forEach((r) => {
      const listing = listingById.get(String(r.listing_id));
      if (!listing) return;

      const material =
        listing?.material ||
        listing?.materialType ||
        listing?.material_name ||
        listing?.material_type ||
        "Unknown";

      const qty = Number(r?.weight ?? r?.quantity ?? listing?.weight ?? listing?.quantity);
      if (!Number.isFinite(qty)) return;

      agg.set(material, (agg.get(material) || 0) + qty);
    });

    return Array.from(agg.entries())
      .map(([material, qty]) => ({
        material: String(material),
        qty: Number(Number(qty).toFixed(2)),
      }))
      .sort((a, b) => a.material.localeCompare(b.material));
  }, [deliveredRequestsInReportRange, listingById]);

  /* ---------------------------------------------------------------------------
     emissionsTrendInReportRange
  --------------------------------------------------------------------------- */
  const emissionsTrendInReportRange = useMemo(() => {
    const buckets = new Map();

    deliveredRequestsInReportRange.forEach((r) => {
      const deliveredAt = parseDateSafe(r.date_delivered);
      if (!deliveredAt) return;

      const listing = listingById.get(String(r.listing_id));
      if (!listing) return;

      const material =
        listing?.material ||
        listing?.materialType ||
        listing?.material_name ||
        listing?.material_type ||
        "Unknown";

      const qty = Number(r?.weight ?? r?.quantity ?? listing?.weight ?? listing?.quantity);
      if (!Number.isFinite(qty)) return;

      const avoided = computeAvoidedEmissionsNum(material, qty);
      if (avoided == null) return;

      const key = bucketKey(deliveredAt, emissionsPeriod);
      buckets.set(key, (buckets.get(key) || 0) + avoided);
    });

    return Array.from(buckets.entries())
      .map(([bucket, avoided]) => ({
        bucket,
        avoided: asNiceNumber(avoided, 6),
      }))
      .sort((a, b) => String(a.bucket).localeCompare(String(b.bucket)));
  }, [
    deliveredRequestsInReportRange,
    listingById,
    computeAvoidedEmissionsNum,
    emissionsPeriod,
  ]);

  /* ---------------------------------------------------------------------------
     pickupsTrendInReportRange
  --------------------------------------------------------------------------- */
  const pickupsTrendInReportRange = useMemo(() => {
    const buckets = new Map();

    deliveredRequestsInReportRange.forEach((r) => {
      const deliveredAt = parseDateSafe(r.date_delivered);
      if (!deliveredAt) return;

      const key = bucketKey(deliveredAt, emissionsPeriod);

      if (pickupsMetric === "count") {
        buckets.set(key, (buckets.get(key) || 0) + 1);
        return;
      }

      const listing = listingById.get(String(r.listing_id));
      const qty = Number(r?.weight ?? r?.quantity ?? listing?.weight ?? listing?.quantity);
      if (!Number.isFinite(qty)) return;

      buckets.set(key, (buckets.get(key) || 0) + qty);
    });

    return Array.from(buckets.entries())
      .map(([bucket, value]) => ({ bucket, value }))
      .sort((a, b) => String(a.bucket).localeCompare(String(b.bucket)));
  }, [deliveredRequestsInReportRange, emissionsPeriod, pickupsMetric, listingById]);

  /* ---------------------------------------------------------------------------
     donorTotals
  --------------------------------------------------------------------------- */
  const donorTotals = useMemo(() => {
    const rows = donorRowsFiltered || [];
    let totalQty = 0;
    let totalAvoided = 0;

    for (const r of rows) {
      const q = numFromAny(r?.qty);
      const a = numFromAny(r?.avoided_emissions);
      if (q != null) totalQty += q;
      if (a != null) totalAvoided += a;
    }

    return { totalQty, totalAvoided };
  }, [donorRowsFiltered]);

  /* ---------------------------------------------------------------------------
     sankeyData
  --------------------------------------------------------------------------- */
  const sankeyData = useMemo(() => {
    const TOP_DONORS = 12;
    const TOP_MATERIALS = 14;

    const matsFilter = new Set((filters.appliedMaterials || []).map(String));
    const donsFilter = new Set((filters.appliedDonors || []).map(String));
    const donorSearch = String(filters.appliedDonorSearch || "").trim().toLowerCase();

    const donorTotalsMap = new Map();
    const materialTotalsMap = new Map();
    const rows = [];

    for (const r of deliveredRequestsInReportRange || []) {
      const listing = listingById.get(String(r.listing_id));
      if (!listing) continue;

      const material =
        listing?.material ||
        listing?.materialType ||
        listing?.material_name ||
        listing?.material_type ||
        "Unknown";

      const donor =
        listing?.company ||
        listing?.company_name ||
        listing?.donor ||
        listing?.donor_company ||
        "—";

      if (!donor || donor === "—") continue;

      if (matsFilter.size > 0 && !matsFilter.has(String(material))) continue;
      if (donsFilter.size > 0 && !donsFilter.has(String(donor))) continue;

      if (donorSearch) {
        const hay = `${donor} ${material}`.toLowerCase();
        if (!hay.includes(donorSearch)) continue;
      }

      const qty = Number(r?.weight ?? r?.quantity ?? listing?.weight ?? listing?.quantity);
      if (!Number.isFinite(qty) || qty <= 0) continue;

      const avoided = computeAvoidedEmissionsNum(material, qty);

      rows.push({ donor: String(donor), material: String(material), qty, avoided });

      donorTotalsMap.set(donor, (donorTotalsMap.get(donor) || 0) + qty);
      materialTotalsMap.set(material, (materialTotalsMap.get(material) || 0) + qty);
    }

    const topDonors = new Set(
      [...donorTotalsMap.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, TOP_DONORS)
        .map(([k]) => String(k))
    );

    const topMaterials = new Set(
      [...materialTotalsMap.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, TOP_MATERIALS)
        .map(([k]) => String(k))
    );

    const donorLabel = (d) => (topDonors.has(d) ? d : t("ghg.sankey.otherDonors"));
    const materialLabel = (m) => (topMaterials.has(m) ? m : t("ghg.sankey.otherMaterials"));

    const nodes = [];
    const nodeIndex = new Map();

    const ensureNode = (key, label) => {
      if (nodeIndex.has(key)) return nodeIndex.get(key);
      const idx = nodes.length;
      nodes.push({ name: label });
      nodeIndex.set(key, idx);
      return idx;
    };

    const qtyNodeKey = "impact:qty";
    const avoidedNodeKey = "impact:avoided";
    ensureNode(qtyNodeKey, t("ghg.sankey.quantity"));
    ensureNode(avoidedNodeKey, t("ghg.sankey.avoided", { unit: DEFAULT_UNIT }));

    const qtyLinksMap = new Map();
    const avoidedLinksMap = new Map();

    const addTo = (map, srcKey, tgtKey, v) => {
      if (!Number.isFinite(v) || v <= 0) return;
      const k = `${srcKey}|||${tgtKey}`;
      map.set(k, (map.get(k) || 0) + v);
    };

    for (const row of rows) {
      const donor = donorLabel(row.donor);
      const material = materialLabel(row.material);

      const donorKey = `donor:${donor}`;
      const matKey = `mat:${material}`;

      ensureNode(donorKey, donor);
      ensureNode(matKey, material);

      addTo(qtyLinksMap, donorKey, matKey, row.qty);
      if (row.avoided != null) addTo(avoidedLinksMap, donorKey, matKey, row.avoided);

      addTo(qtyLinksMap, matKey, qtyNodeKey, row.qty);
      if (row.avoided != null) addTo(avoidedLinksMap, matKey, avoidedNodeKey, row.avoided);
    }

    const mapToLinks = (map) => {
      const links = [];
      for (const [k, v] of map.entries()) {
        if (!Number.isFinite(v) || v <= 0) continue;
        const [srcKey, tgtKey] = k.split("|||");
        const source = nodeIndex.get(srcKey);
        const target = nodeIndex.get(tgtKey);
        if (source == null || target == null) continue;
        links.push({ source, target, value: v });
      }
      return links;
    };

    return {
      qty: { nodes, links: mapToLinks(qtyLinksMap) },
      avoided: { nodes, links: mapToLinks(avoidedLinksMap) },
    };
  }, [
    deliveredRequestsInReportRange,
    listingById,
    filters.appliedMaterials,
    filters.appliedDonors,
    filters.appliedDonorSearch,
    computeAvoidedEmissionsNum,
    t,
  ]);

  /* =============================================================================
     ACTIONS (CRUD)
  ============================================================================= */

  const addReceiverRow = useCallback(() => {
    setReceiverRows((p) => [
      ...p,
      {
        row_id: nextRowId(p),
        name: "",
        emission_factor: "",
        recycling_emissions: "",
        energy_consumption_kwh: "",
        waste_stage_qty: "",
      },
    ]);
  }, []);

  const deleteReceiverRow = useCallback(() => {
    const ids = new Set(receiverSelection);
    setReceiverRows((p) => p.filter((r) => !ids.has(r.row_id)));
    setReceiverSelection([]);
  }, [receiverSelection]);

  const saveReceiverRows = useCallback(async () => {
    setReceiverSaving(true);
    setError("");

    try {
      const payload = receiverRows.map((r) => ({
        name: r.name,
        emission_factor: r.emission_factor,
        recycling_emissions: r.recycling_emissions,
        emission_unit: DEFAULT_UNIT,
        energy_consumption_kwh: r.energy_consumption_kwh,
        waste_stage_qty: r.waste_stage_qty,
      }));

      await postJson(API.receiver.save, { rows: payload });
    } catch {
      setError(t("ghg.errors.saveReceiverFailed"));
    } finally {
      setReceiverSaving(false);
    }
  }, [receiverRows, t]);

  const addDonorRow = useCallback(() => {
    setDonorRows((p) => [
      ...p,
      {
        row_id: nextRowId(p),
        donor: "",
        material_type: "",
        description: "",
        qty: "",
        avoided_emissions: "",
      },
    ]);
  }, []);

  const deleteDonorRow = useCallback(() => {
    const ids = new Set(donorSelection);
    setDonorRows((p) => p.filter((r) => !ids.has(r.row_id)));
    setDonorSelection([]);
  }, [donorSelection]);

  const saveDonorRows = useCallback(async () => {
    setDonorSaving(true);
    setError("");

    try {
      await postJson(API.donor.save, { rows: donorRows });
    } catch {
      setError(t("ghg.errors.donorSaveUnavailable"));
    } finally {
      setDonorSaving(false);
    }
  }, [donorRows, t]);

  /* =============================================================================
     EXPORT PPT
  ============================================================================= */

  const [exportMenuOpen, setExportMenuOpen] = useState(false);

  const waitNextPaint = () =>
    new Promise((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(resolve))
    );

  const ensureSankeyExpandedForExport = useCallback(async () => {
    const ref = sankeyAccordionRef.current;

    if (ref && typeof ref.expand === "function") {
      ref.expand();
      await waitNextPaint();
      return;
    }
    if (ref && typeof ref.open === "function") {
      ref.open();
      await waitNextPaint();
      return;
    }
    if (ref && typeof ref.setExpanded === "function") {
      ref.setExpanded(true);
      await waitNextPaint();
      return;
    }

    const el = ref && ref.nodeType === 1 ? ref : null;
    if (el) {
      const toggle =
        el.querySelector?.('[aria-expanded="false"]') ||
        el.querySelector?.("button[aria-expanded]") ||
        el.querySelector?.(".MuiAccordionSummary-root");

      if (toggle && typeof toggle.click === "function") {
        toggle.click();
        await waitNextPaint();
      }
    }
  }, []);

  const onExportReportPpt = useCallback(
    async (exportOpts) => {
      const prevPickupsMetric = pickupsMetric;

      const qtyEl = sankeyQtyRef.current;
      const avoidedEl = sankeyAvoidedRef.current;

      const prevQtyDisplay = qtyEl?.style?.display;
      const prevAvoidedDisplay = avoidedEl?.style?.display;

      const wantsPickups = Boolean(exportOpts?.includePickups);
      const wantsAnySankey =
        Boolean(exportOpts?.includeSankeyQty) ||
        Boolean(exportOpts?.includeSankeyAvoided);

      try {
        setError("");
        setIsExporting(true);

        if (wantsAnySankey) {
          await ensureSankeyExpandedForExport();
        }

        if (wantsPickups && pickupsMetric !== "qty") {
          setPickupsMetric("qty");
          await waitNextPaint();
        }

        if (wantsAnySankey) {
          if (exportOpts.includeSankeyQty && qtyEl) qtyEl.style.display = "block";
          if (exportOpts.includeSankeyAvoided && avoidedEl) avoidedEl.style.display = "block";

          await waitNextPaint();
          await waitNextPaint();
        }

        await exportReportPpt({
          reportFromStr,
          reportToStr,
          darkMode,
          emissionsPeriod,
          receiverMaterialQuantities,
          emissionsTrendInReportRange,

          trendChartEl: trendChartRef.current,

          pickupsChartEl: pickupsChartRef.current,
          pickupsTitle: t("ghg.export.pickupsTitle"),
          pickupsSubtitle: t("ghg.export.pickupsSubtitle"),
          pickupsMetricLabel: t("ghg.sankey.quantity"),

          sankeyQtyEl: sankeyQtyRef.current,
          sankeyAvoidedEl: sankeyAvoidedRef.current,

          unitLabel: DEFAULT_UNIT,
          exportOptions: exportOpts,
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        setError(t("ghg.errors.exportFailed"));
      } finally {
        if (qtyEl) qtyEl.style.display = prevQtyDisplay ?? "";
        if (avoidedEl) avoidedEl.style.display = prevAvoidedDisplay ?? "";

        setIsExporting(false);

        if (wantsPickups && prevPickupsMetric !== pickupsMetric) {
          setPickupsMetric(prevPickupsMetric);
        }
      }
    },
    [
      pickupsMetric,
      reportFromStr,
      reportToStr,
      darkMode,
      emissionsPeriod,
      receiverMaterialQuantities,
      emissionsTrendInReportRange,
      ensureSankeyExpandedForExport,
      t,
    ]
  );

  /* =============================================================================
     PERMISSION / LOADING GUARDS
  ============================================================================= */

  if (isChecking) {
    return (
      <LoadingSpinner
        fullPage
        size={50}
        message={t("ghg.checkingPermissions")}
      />
    );
  }

  if (!hasPermission) return null;

  if (initialLoading) {
    return (
      <LoadingSpinner
        fullPage
        size={50}
        message={t("ghg.loadingHub")}
      />
    );
  }

  if (filters.hasSearched && invLoading) {
    return (
      <LoadingSpinner
        fullPage
        size={50}
        message={t("ghg.loadingHub")}
      />
    );
  }

  const receiverRowCount = receiverRowsFiltered.length;
  const donorRowCount = donorRowsFiltered.length;

  /* =============================================================================
     RENDER
  ============================================================================= */

  return (
    <>
      <ExportPptDialog
        open={exportMenuOpen}
        onClose={() => setExportMenuOpen(false)}
        hasSearched={filters.hasSearched}
        reportFromStr={reportFromStr}
        reportToStr={reportToStr}
        setReportFromStr={setReportFromStr}
        setReportToStr={setReportToStr}
        onExport={onExportReportPpt}
      />

      <Box
        ref={rootRef}
        className={`rxghg_root ${darkMode ? "isDark" : "isLight"} ${
          isVisible ? "isVisible" : ""
        }`}
      >
        <div className="page-header-row">
          <div />
          <LastUpdatedBadge
            timestamp={invLastUpdated}
            onRefresh={handleRefresh}
            isRefreshing={refreshing}
            variant="receiver"
          />
        </div>

        <HeroSection
          receiverRowCount={receiverRowCount}
          donorRowCount={donorRowCount}
          onExportReportPpt={() => setExportMenuOpen(true)}
        />

        {error ? (
          <Box className="rxghg_block">
            <Alert message={error} severity="error" />
          </Box>
        ) : null}

        {invError && filters.hasSearched ? (
          <Box className="rxghg_block">
            <Alert message={String(invError)} severity="error" />
          </Box>
        ) : null}

        {metaError ? (
          <Box className="rxghg_block">
            <Alert message={String(metaError)} severity="error" />
          </Box>
        ) : null}

        {metaLoading ? (
          <Box className="rxghg_block">
            <LoadingSpinner size={24} message={t("ghg.loadingFilters")} />
          </Box>
        ) : null}

        <Box className="rxghg_block">
          <RxGhgFilters
            materialOptions={materialOptions}
            donorOptions={donorOptions}
            draftMaterials={filters.draftMaterials}
            setDraftMaterials={filters.setDraftMaterials}
            draftDonors={filters.draftDonors}
            setDraftDonors={filters.setDraftDonors}
            draftDonorSearch={filters.draftDonorSearch}
            setDraftDonorSearch={filters.setDraftDonorSearch}
            draftReceiverSearch={filters.draftReceiverSearch}
            setDraftReceiverSearch={filters.setDraftReceiverSearch}
            draftReceiverOnlyUsed={filters.draftReceiverOnlyUsed}
            setDraftReceiverOnlyUsed={filters.setDraftReceiverOnlyUsed}
            draftEfMin={filters.draftEfMin}
            setDraftEfMin={filters.setDraftEfMin}
            draftEfMax={filters.draftEfMax}
            setDraftEfMax={filters.setDraftEfMax}
            draftReMin={filters.draftReMin}
            setDraftReMin={filters.setDraftReMin}
            draftReMax={filters.draftReMax}
            setDraftReMax={filters.setDraftReMax}
            draftNetMin={filters.draftNetMin}
            setDraftNetMin={filters.setDraftNetMin}
            draftNetMax={filters.draftNetMax}
            setDraftNetMax={filters.setDraftNetMax}
            draftQtyMin={filters.draftQtyMin}
            setDraftQtyMin={filters.setDraftQtyMin}
            draftQtyMax={filters.draftQtyMax}
            setDraftQtyMax={filters.setDraftQtyMax}
            draftAvoidedMin={filters.draftAvoidedMin}
            setDraftAvoidedMin={filters.setDraftAvoidedMin}
            draftAvoidedMax={filters.draftAvoidedMax}
            setDraftAvoidedMax={filters.setDraftAvoidedMax}
            onApply={onApplyFilters}
            onClear={onClearFilters}
          />
        </Box>

        {!filters.hasSearched ? (
          <Box className="rxghg_block">
            <Alert severity="info" message={t("ghg.applyToSeeResults")} />
          </Box>
        ) : (
          <>
            <ReceiverFactorsPanel
              receiverRowsFiltered={receiverRowsFiltered}
              receiverRows={receiverRows}
              setReceiverRows={setReceiverRows}
              receiverSelection={receiverSelection}
              setReceiverSelection={setReceiverSelection}
              receiverRowCount={receiverRowCount}
              receiverSaving={receiverSaving}
              addReceiverRow={addReceiverRow}
              deleteReceiverRow={deleteReceiverRow}
              saveReceiverRows={saveReceiverRows}
            />

            <DonorAllocationsPanel
              donorRowsFiltered={donorRowsFiltered}
              donorRows={donorRows}
              setDonorRows={setDonorRows}
              donorSelection={donorSelection}
              setDonorSelection={setDonorSelection}
              donorRowCount={donorRowCount}
              donorSaving={donorSaving}
              donorTotals={donorTotals}
              addDonorRow={addDonorRow}
              deleteDonorRow={deleteDonorRow}
              saveDonorRows={saveDonorRows}
              computeAvoidedEmissions={computeAvoidedEmissions}
              unitLabel={DEFAULT_UNIT}
            />

            <TrendChartPanel
              emissionsTrendInReportRange={emissionsTrendInReportRange}
              emissionsPeriod={emissionsPeriod}
              setEmissionsPeriod={setEmissionsPeriod}
              isExporting={isExporting}
              trendChartRef={trendChartRef}
              unitLabel={DEFAULT_UNIT}
            />

            <PickupsChartPanel
              pickupsTrendInReportRange={pickupsTrendInReportRange}
              pickupsMetric={pickupsMetric}
              setPickupsMetric={setPickupsMetric}
              emissionsPeriod={emissionsPeriod}
              setEmissionsPeriod={setEmissionsPeriod}
              isExporting={isExporting}
              pickupsChartRef={pickupsChartRef}
            />

            <SankeyPanel
              sankeyData={sankeyData}
              darkMode={darkMode}
              unitLabel={DEFAULT_UNIT}
              sankeyQtyRef={sankeyQtyRef}
              sankeyAvoidedRef={sankeyAvoidedRef}
              sankeyAccordionRef={sankeyAccordionRef}
            />

            <MaterialsChipsPanel receiverMaterialQuantities={receiverMaterialQuantities} />
          </>
        )}
      </Box>
    </>
  );
}
